import { useEffect, useState, type ReactNode } from 'react'
import { t } from '@/lib/i18n'

export const TOAST_MS = 5000

type ToastAction = { label: string; fn: () => void }
export type ToastOptions = { undo?: () => void; alt?: ToastAction }
type ToastItem = { id: number; msg: string; undo?: () => void; alt?: ToastAction }

// 模块作用域的单个订阅者，不是 Context。
//
// 两个理由：① 调用点是 router.post 的 onSuccess 回调、事件处理函数这些**不在
// render 里**的地方，useContext 拿不到；② 全应用只有一个 <ToastHost>，Provider
// 那一层除了包一圈什么都不做。同一个模式已经用在 Celebrate.tsx 的 fanfaredCards 上。
let subscriber: ((item: ToastItem) => void) | null = null
let seq = 0

/**
 * 弹一条 toast。
 *
 *   toast('刷牙 +3 分', { undo: () => router.delete(...) })
 *   toast('已兑换「看电影」', { undo: refund, alt: { label: '去商店', fn: goStore } })
 *
 * 5 秒后自动消失。**每个写操作都该带 undo**（规矩 §4③），唯一的例外是删除——
 * 删除不给撤销，所以它前面加二次确认 sheet。
 */
export function toast(msg: string, opts: ToastOptions = {}): void {
  seq += 1
  subscriber?.({ id: seq, msg, undo: opts.undo, alt: opts.alt })
}

/**
 * toast 的宿主。Layout 里渲染一次。
 *
 * 一次只显示一条：手机上宽度只够一行字加一个动作按钮，堆两条会把 tab bar 盖住。
 * 后来的顶掉前面那条 —— 而不是排队等 5 秒，那会让「刚做的事」的撤销按钮迟到 5 秒。
 */
export function ToastHost() {
  const [item, setItem] = useState<ToastItem | null>(null)

  useEffect(() => {
    subscriber = setItem
    return () => {
      subscriber = null
    }
  }, [])

  useEffect(() => {
    if (!item) return
    const timer = setTimeout(() => setItem(null), TOAST_MS)
    return () => clearTimeout(timer)
  }, [item])

  const shown = item !== null

  return (
    <div
      role="status"
      aria-live="polite"
      className={
        'fixed left-3 right-3 z-[60] above-tabbar flex items-center gap-2.5 ' +
        'bg-ink text-bg rounded-ctl pl-3.5 pr-2 py-3 overflow-hidden ' +
        'shadow-[0_10px_30px_rgba(0,0,0,.28)] transition-[transform,opacity] duration-300 ease-sheet ' +
        (shown ? 'translate-y-0 opacity-100' : 'translate-y-[160%] opacity-0 pointer-events-none')
      }
    >
      <span className="flex-1 min-w-0 text-sm font-semibold truncate">{item?.msg}</span>

      {item?.alt && (
        <ToastAct
          tone="alt"
          onClick={() => {
            const { fn } = item.alt!
            setItem(null)
            fn()
          }}
        >
          {item.alt.label}
        </ToastAct>
      )}

      {item?.undo && (
        <ToastAct
          tone="undo"
          onClick={() => {
            const { undo } = item
            setItem(null)
            undo!()
          }}
        >
          {t('toast.undo')}
        </ToastAct>
      )}

      {/* key 换了就重新挂载 ⇒ 动画从头跑。稿子里那句 `void bar.offsetWidth` 就是
          在 React 之外重启动画的等价写法。 */}
      {shown && <span key={item.id} className="absolute left-0 bottom-0 h-[2.5px] bg-toast-act toast-bar" />}
    </div>
  )
}

function ToastAct({ tone, onClick, children }: {
  tone: 'undo' | 'alt'
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'shrink-0 min-h-tap px-3 rounded-chip font-extrabold text-sm whitespace-nowrap ' +
        'active:bg-white/10 ' +
        (tone === 'alt' ? 'text-toast-act-alt' : 'text-toast-act')
      }
    >
      {children}
    </button>
  )
}
